/**
 * BrandIconsEditor Component
 * 3x2 Grid editor for sponsor brand icons with editable titles
 */

import React from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import type { SponsorBrandConfig } from '../types';
import { readFileAsDataUrl } from '../utils';

interface BrandIconsEditorProps {
    brands: SponsorBrandConfig[];
    onChange: (brands: SponsorBrandConfig[]) => void;
    accept?: string;
    disabled?: boolean;
}

export function BrandIconsEditor({
    brands,
    onChange,
    accept = 'image/*',
    disabled = false,
}: BrandIconsEditorProps) {
    const handleBrandUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
        if (!file.type.startsWith('image/') && !isSvg) {
            toast.error('กรุณาเลือกไฟล์รูปภาพ (PNG, JPG)');
            event.target.value = '';
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            const updated = [...brands];
            if (updated[index]) {
                updated[index] = { ...updated[index], image: dataUrl };
                onChange(updated);
            }
        } catch (error) {
            console.error('Failed to read brand icon file', error);
            toast.error('ไม่สามารถอ่านไฟล์ได้');
        } finally {
            event.target.value = '';
        }
    };

    const handleBrandClear = (index: number) => {
        const updated = [...brands];
        if (updated[index]) {
            updated[index] = { ...updated[index], image: '', title: '' };
            onChange(updated);
        }
    };

    const handleTitleChange = (index: number, val: string) => {
        const updated = [...brands];
        if (updated[index]) {
            updated[index] = { ...updated[index], title: val };
            onChange(updated);
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
                {brands.map((brand, index) => (
                    <div key={brand.id} className="flex flex-col gap-3 p-3 rounded-xl border border-stone-100 dark:border-stone-700 bg-white dark:bg-stone-800/50 shadow-sm">
                        {/* Image Upload Area */}
                        <div className="relative w-full aspect-square rounded-lg border-2 border-dashed border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 overflow-hidden group hover:border-yellow-500 dark:hover:border-orange-500 transition-all">
                            {brand.image ? (
                                <>
                                    <img
                                        src={brand.image}
                                        alt={brand.title || `Brand ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        <label className="cursor-pointer px-2 py-1 bg-white/90 rounded text-[10px] font-bold text-stone-700 hover:bg-white uppercase tracking-wider">
                                            เปลี่ยน
                                            <input
                                                type="file"
                                                accept={accept}
                                                onChange={(e) => handleBrandUpload(index, e)}
                                                disabled={disabled}
                                                className="hidden"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleBrandClear(index)}
                                            disabled={disabled}
                                            className="px-2 py-1 bg-red-500/90 rounded text-[10px] font-bold text-white hover:bg-red-600 uppercase tracking-wider"
                                        >
                                            ลบ
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-stone-400 dark:text-stone-500 hover:text-yellow-600 dark:hover:text-orange-500 transition-colors">
                                    <Plus className="h-10 w-10 mb-2" />
                                    <span className="text-xs font-semibold">อัปโหลดรูปแบรนด์</span>
                                    <input
                                        type="file"
                                        accept={accept}
                                        onChange={(e) => handleBrandUpload(index, e)}
                                        disabled={disabled}
                                        className="hidden"
                                    />
                                </label>
                            )}
                        </div>

                        {/* Brand Name Input */}
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider">ชื่อแบรนด์ (ที่จะใช้เป็น Tag)</label>
                            <input
                                type="text"
                                value={brand.title}
                                onChange={(e) => handleTitleChange(index, e.target.value)}
                                placeholder="ระบุชื่อแบรนด์..."
                                disabled={disabled}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 transition-all disabled:opacity-50"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/50 p-3">
                <p className="text-xs text-yellow-800 dark:text-yellow-400 leading-relaxed">
                    <strong>💡 ข้อมูลสำคัญ:</strong> ชื่อแบรนด์ที่คุณตั้งไว้จะปรากฏเป็น <strong>Tag สินค้า</strong> ในหน้าจัดการสินค้าให้เลือกโดยอัตโนมัติ และในแอปมือถือจะแสดงผลเรียงเป็นแถวละ 3 แบรนด์ เมื่อลูกค้ากดจะกรองสินค้าตาม Tag นั้นๆ
                </p>
            </div>
        </div>
    );
}
